import { FC } from 'react'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { gpConnectConstants } from 'locale/gpConnect'
import { GPConnectBanner, SupersededData, Resident } from '@lmc/ts-common'
import { getNHSFormattedNumber } from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import useHomeType from 'hooks/useHomeType'
import {
  DOBWarning,
  FamilyNameWarning,
  GenderWarning,
  GivenNameWarning,
  SupersededWarning,
} from './GPBannerWarnings'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type Props = {
  bannerData: GPConnectBanner
  supersededData?: SupersededData
  residentId?: string
}

export const GPBanner: FC<Props> = ({
  bannerData,
  supersededData,
  residentId,
}) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  const { isFromCanada } = useHomeType()

  const residents = useSelector(
    (state: { data: { residents: Resident[] } }) => state.data.residents
  )
  const resident: Resident | undefined = residents?.find(
    (res) => res.id === residentId
  )

  if (!bannerData) {
    return (
      <Container>
        <Banner />
      </Container>
    )
  }

  const {
    prefix,
    familyName,
    givenName,
    gender,
    dateOfBirth,
    nhsNumber,
    address,
    phone,
    gpDetails,
    pass,
  } = bannerData
  const residentDateOfBirth = dayjs.utc(resident?.dateOfBirth)
  const gpDateOfBirth = dayjs.utc(dateOfBirth)

  return (
    <Container>
      <Banner>
        <TopSection>
          <TableCell>
            <Text weight="bold">{`${prefix} ${givenName} ${familyName} `}</Text>
            {!isSameData(familyName, resident?.name.last ?? '') && (
              <FamilyNameWarning
                familyName={resident?.name.last}
                residentId={residentId}
              />
            )}
            {!isSameData(givenName, resident?.name.first ?? '') && (
              <GivenNameWarning
                givenName={resident?.name.first}
                residentId={residentId}
              />
            )}
          </TableCell>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.GENDER)}</StyledDesc>
              {gender}
            </Text>
            {!isSameData(gender, resident?.gender ?? '') && (
              <GenderWarning
                gender={resident?.gender}
                residentId={residentId}
              />
            )}
          </TableCell>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.DOB)}</StyledDesc>
              {gpDateOfBirth.format('DD/MM/YYYY')}
            </Text>
            {!gpDateOfBirth.isSame(residentDateOfBirth) && (
              <DOBWarning
                dob={residentDateOfBirth.format('DD/MM/YYYY')}
                residentId={residentId}
              />
            )}
          </TableCell>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.NHS_NO)}</StyledDesc>
              {supersededData
                ? supersededData.supersededNHSNumber &&
                  getNHSFormattedNumber(
                    supersededData.supersededNHSNumber,
                    isFromCanada
                  )
                : nhsNumber && getNHSFormattedNumber(nhsNumber, isFromCanada)}
              <SupersededWarning
                recordsNHSNumber={supersededData?.recordsNHSNumber ?? ''}
                isFromCanada={isFromCanada}
                residentId={residentId}
              />
            </Text>
          </TableCell>
        </TopSection>
        <BottomSection>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.ADDRESS)}</StyledDesc>
              {address}
            </Text>
          </TableCell>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.CONTACT)}</StyledDesc>
              {phone}
            </Text>
          </TableCell>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.GP_DETAILS)}</StyledDesc>
              {gpDetails}
            </Text>
          </TableCell>
          <TableCell>
            <Text weight="bold">
              <StyledDesc>{t(gpConnectConstants.PAS_NO)}</StyledDesc>
              {pass}
            </Text>
          </TableCell>
        </BottomSection>
      </Banner>
    </Container>
  )
}

const normalizeString = (input: string) => {
  return input.trim().toLowerCase()
}

const isSameData = (firstData: string, secondData: string) => {
  const normalizedFirstData = normalizeString(firstData)
  const normalizedSecondData = normalizeString(secondData)

  return normalizedFirstData === normalizedSecondData
}

const Container = styled.div`
  border-radius: 16px;
  border: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  overflow: hidden;
  margin-top: 14px;
  margin-bottom: 24px;
`

const Banner = styled.table`
  background-color: ${(props) => props.theme.color.neutral.color10};
  padding: 24px;
  width: 100%;
  min-height: 122px;
`

const TableCell = styled.td`
  vertical-align: top;
  padding: 18px;
  width: 25%;
`

const TopSection = styled.tr`
  border-bottom: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin: 24px;
  padding-bottom: 16px;
`

const StyledDesc = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const BottomSection = styled.tr`
  margin: 12px;
  padding-top: 16px;
`
